import { isSome, Maybe, noop } from '@crocodille/common';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  Children,
} from 'react';
import { useMemo } from 'react';
import Button from '../../elements/button/button';
import CrossIcon from '../../icons/cross-icon';
import CartQuantity from '../cart-quantity/cart-quantity';
import * as routing from '../../../routing';
import cn from 'classnames';

import styles from './cart-list.module.scss';
import SpinnerIcon from '../../icons/spinner-icon';
import { formatCurrency } from '@utils/format';

export interface CartListProps {
  readonly busy?: boolean;
  readonly disabled?: boolean;
  readonly onQuantityChange?: QuantityChangeHandler;
  readonly onRemove?: (orderItemId: number) => void;
  readonly total: number;
  readonly minOrderValue?: Maybe<number>;
}

export type QuantityChangeHandler = (
  orderItemId: number,
  quantity: number,
) => void;

const CartListContext = createContext<{
  handleQuantityChange: QuantityChangeHandler;
  handleRemove: (orderItemId: number) => void;
}>({
  handleQuantityChange: noop,
  handleRemove: noop,
});

const CartList: React.FC<React.PropsWithChildren<CartListProps>> = ({
  children,
  busy = false,
  disabled = false,
  onQuantityChange = noop,
  onRemove = noop,
  total,
  minOrderValue,
}) => {
  const { t } = useTranslation();
  const { push, route, locale } = useRouter();

  const busyRef = useRef(busy);
  useEffect(() => {
    busyRef.current = busy;
  }, [busy]);

  const handleQuantityChange = useCallback(
    (orderItemId: number, quantity: number) => {
      if (busyRef.current) {
        return;
      }

      onQuantityChange(orderItemId, quantity);
    },
    [onQuantityChange],
  );

  const handleRemove = useCallback(
    (orderItemId: number) => {
      if (busyRef.current) {
        return;
      }

      onRemove(orderItemId);
    },
    [onRemove],
  );

  const ctx = useMemo(
    () => ({ handleRemove, handleQuantityChange }),
    [handleQuantityChange, handleRemove],
  );

  return (
    <CartListContext.Provider value={ctx}>
      <div className={styles.container}>
        <h2 className={styles.title}>{t('cart.your_order')}</h2>
        {isSome(minOrderValue) ? (
          <p className={styles.minOrderValue}>
            {t('cart.min_order_value', {
              value: formatCurrency(minOrderValue, 'CZK', locale),
            })}
          </p>
        ) : null}
        <table className={styles.cart}>
          <thead hidden={true}>
            <tr className={styles.cartHead}>
              <th className={styles.productColumn}>{t('cart.product')}</th>
              <th className={styles.quantityColumn}>{t('cart.quantity')}</th>
              <th className={styles.priceColumn}>{t('cart.price')}</th>
              <th className={styles.actionColumn}>{t('cart.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {Children.count(children) === 0 ? (
              <tr className={styles.emptyCart}>
                <td colSpan={4}>{t('cart.empty')}</td>
              </tr>
            ) : (
              children
            )}
          </tbody>
          <tfoot>
            <tr className={styles.cartFoot}>
              <th className={styles.totalColumn} colSpan={2}>
                {t('cart.total')}
              </th>
              <td className={styles.totalPriceColumn} colSpan={2}>
                {formatCurrency(total, 'CZK', locale)}
              </td>
            </tr>
          </tfoot>
        </table>
        {routing.isCheckoutProcessRoute(route) ? null : (
          <Button
            className={styles.checkoutButton}
            display="block"
            size="large"
            disabled={disabled}
            onClick={() => push(routing.checkoutProcess())}
          >
            {t('cart.checkout')}
            <Button.Icon>{busy ? <SpinnerIcon /> : null}</Button.Icon>
          </Button>
        )}
      </div>
    </CartListContext.Provider>
  );
};

export interface CartListItemProps {
  readonly orderItemId?: number;
  readonly productName: string;
  readonly quantity?: number;
  readonly editDisabled?: boolean;
  readonly price?: Maybe<number>;
  readonly child?: boolean;
}

export const CartListItem: React.FC<
  React.PropsWithChildren<CartListItemProps>
> = ({
  orderItemId,
  productName,
  quantity,
  editDisabled = false,
  price,
  child = false,
}) => {
  const { t } = useTranslation();
  const { locale } = useRouter();
  const { handleQuantityChange, handleRemove } = useContext(CartListContext);

  const onQuantityChange = useCallback(
    (quantity: number) => {
      if (orderItemId == null) {
        return;
      }

      handleQuantityChange(orderItemId, quantity);
    },
    [handleQuantityChange, orderItemId],
  );

  const removeItem = useCallback(() => {
    if (orderItemId == null) {
      return;
    }

    handleRemove(orderItemId);
  }, [handleRemove, orderItemId]);

  return (
    <tr className={cn(styles.item, { [styles.isChild]: child })}>
      <td className={styles.actionsColumn}>
        {!editDisabled && (
          <button
            className={styles.removeButton}
            type="button"
            aria-label={t('cart.remove_item')}
            onClick={removeItem}
            disabled={editDisabled}
          >
            <CrossIcon />
          </button>
        )}
      </td>
      <td className={styles.productColumn} colSpan={child ? 2 : 1}>
        {productName}
      </td>
      {!child ? (
        <td className={styles.quantityColumn}>
          {isSome(quantity) ? (
            <CartQuantity
              value={quantity}
              onChange={onQuantityChange}
              disabled={editDisabled}
            />
          ) : null}
        </td>
      ) : null}
      <td className={styles.priceColumn}>
        {isSome(price) ? formatCurrency(price, 'CZK', locale) : null}
      </td>
    </tr>
  );
};

export default CartList;
